<template>
    <button
        type="button"
        :class="cn(buttonClass, props.class)"
        :disabled="disabled"
        :aria-label="copied ? copiedLabel : label"
        @click="copy"
    >
        <span class="relative flex size-4 items-center justify-center">
            <Transition
                mode="out-in"
                enter-active-class="transition duration-150 ease-out"
                enter-from-class="scale-50 opacity-0"
                enter-to-class="scale-100 opacity-100"
                leave-active-class="transition duration-100 ease-in"
                leave-from-class="scale-100 opacity-100"
                leave-to-class="scale-50 opacity-0"
            >
                <Check v-if="copied" key="copied" class="size-4 text-new-green-600" />
                <Copy v-else key="idle" class="size-4" />
            </Transition>
        </span>
    </button>
</template>

<script lang="ts" setup>
import { useClipboard } from '@vueuse/core';
import { toast } from 'vue-sonner';
import { Check, Copy } from 'lucide-vue-next';
import { cn } from '@/lib/utils';

const props = withDefaults(
    defineProps<{
        value: string;
        label: string;
        copiedLabel?: string;
        successMessage?: string;
        errorMessage?: string;
        disabled?: boolean;
        class?: string;
    }>(),
    {
        copiedLabel: 'Copied',
        successMessage: 'Copied to clipboard!',
        errorMessage: 'Could not copy to clipboard',
        disabled: false,
        class: undefined,
    }
);

const buttonClass =
    'flex items-center justify-center p-2 text-new-neutral-400 transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-new-neutral-400';

/**
 * Each button owns its own clipboard instance so that `copied` — and therefore
 * the check-mark animation — is scoped to the button that was clicked.
 */
const { copied, copy: writeToClipboard } = useClipboard({ copiedDuring: 1500 });

const copy = async (): Promise<void> => {
    if (props.disabled) {
        return;
    }

    await writeToClipboard(props.value);

    if (!copied.value) {
        toast.error(props.errorMessage);

        return;
    }

    toast.success(props.successMessage);
};
</script>
